import React, { useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import { Service } from "../../../models/ServiceModel";
import ButtonCancelForm from "../../forms/ButtonCancelForm";
import ServiceFormStep1 from "../forms/ServiceFormStep1";
import ServiceFormStep2 from "../forms/ServiceFormStep2";
import ServiceFormStep3 from "../forms/ServiceFormStep3";
import { BulkPassengerCreated } from "../../Passengers/BulkPassengerModal";
import VerticalStepper from "./VerticalStepper";
import ServiceCreationLoadingScreen from "./ServiceCreationLoadingScreen";
import {
  getStepVisualState,
  isStepValidByNumber,
} from "./serviceWizardValidation";
import "./styles/ServiceCreationWizard.css";

type DriverOption = {
  value: number;
  label: string;
  status: string;
  is_available: boolean;
};

type SimpleOption = {
  value: number;
  label: string;
  company: number;
  is_active: boolean;
};

type Props = {
  step: number;
  form: Service;
  isSubmitting: boolean;
  savingLocked: boolean;
  isStepValid: boolean;
  companies: { value: string; label: string }[];
  passengers: any[];
  locations: { value: string; label: string }[];
  drivers: DriverOption[];
  authorizers: SimpleOption[];
  requesters: SimpleOption[];
  costCenters: SimpleOption[];
  fieldErrors: Record<string, string>;
  onFormChange: (field: keyof Service, value: any) => void;
  onFormPatch?: (patch: Partial<Service>) => void;
  onSelectedPassengerChange: (passengerId: number | null) => void;
  onAddSequence: (seq: any[]) => void;
  onPassengersCreated: (created: BulkPassengerCreated[]) => void;
  onPassengerUpdated?: (updated: {
    id: number;
    name: string;
    addresses: { id: number; street: string }[];
  }) => void;
  onStepChange: (step: number) => void;
  onNext: () => void;
  onPrev: () => void;
  onSave: () => Promise<any>;
  onSubmitStart: () => void;
  onCancel: () => void;
};

const ServiceCreationWizard: React.FC<Props> = ({
  step,
  form,
  isSubmitting,
  savingLocked,
  isStepValid,
  companies,
  passengers,
  locations,
  drivers,
  authorizers,
  requesters,
  costCenters,
  fieldErrors,
  onFormChange,
  onFormPatch,
  onSelectedPassengerChange,
  onAddSequence,
  onPassengersCreated,
  onPassengerUpdated,
  onStepChange,
  onNext,
  onPrev,
  onSave,
  onSubmitStart,
  onCancel,
}) => {
  const bodyRef = useRef<HTMLElement>(null);
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  const companyName =
    companies.find((c) => String(c.value) === String(form.company))?.label ?? null;

  const stepStates = useMemo(
    () => ({
      1: getStepVisualState(1, step, form, hasFieldErrors, companyName),
      2: getStepVisualState(2, step, form, hasFieldErrors, companyName),
      3: getStepVisualState(3, step, form, hasFieldErrors, companyName),
    }),
    [step, form, hasFieldErrors, companyName]
  );

  const handleStepClick = (targetStep: number) => {
    if (targetStep === step) return;
    if (targetStep > step && !isStepValid) return;

    for (let s = 1; s < targetStep; s += 1) {
      if (!isStepValidByNumber(s, form, companyName)) return;
    }

    onStepChange(targetStep);
    bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = async () => {
    if (savingLocked || isSubmitting) return;
    flushSync(() => {
      onSubmitStart();
    });
    await onSave();
  };

  return (
    <div className="services-modal">
      <div
        className={`services-modal-inner services-modal-inner--wizard${
          isSubmitting ? " services-modal-inner--submitting" : ""
        }`}
      >
        {isSubmitting ? (
          <ServiceCreationLoadingScreen />
        ) : (
          <>
            <header className="svc-wizard-header">
              <div className="svc-wizard-header__top">
                <h3 className="svc-wizard-header__title">Crear Nuevo Servicio</h3>
                <span className="svc-wizard-header__badge">Paso {step} de 3</span>
              </div>
              <p className="svc-wizard-header__subtitle">
                Configura un nuevo servicio de transporte paso a paso
              </p>
            </header>

            <div className="svc-wizard-layout">
              <aside className="svc-wizard-sidebar">
                <VerticalStepper
                  currentStep={step}
                  stepStates={stepStates}
                  onStepClick={handleStepClick}
                />
              </aside>

              <section ref={bodyRef} className="svc-wizard-main">
                <div key={step} className="svc-wizard-step-content">
                  {step === 1 && (
                    <ServiceFormStep1
                      value={form}
                      onChange={onFormChange}
                      onPatch={onFormPatch}
                      companies={companies}
                    />
                  )}

                  {step === 2 && (
                    <ServiceFormStep2
                      value={form}
                      onChange={(field, value) => {
                        onFormChange(field, value);
                        if (field === "selected_passenger") {
                          onSelectedPassengerChange(value);
                        }
                      }}
                      passengers={passengers}
                      locations={locations}
                      onAddSequence={onAddSequence}
                      companyName={companyName}
                      onPassengersCreated={onPassengersCreated}
                      onPassengerUpdated={onPassengerUpdated}
                    />
                  )}

                  {step === 3 && (
                    <ServiceFormStep3
                      value={form}
                      onChange={onFormChange}
                      drivers={drivers}
                      authorizers={authorizers}
                      requesters={requesters}
                      costCenters={costCenters}
                      fieldErrors={fieldErrors}
                    />
                  )}
                </div>
              </section>
            </div>

            <footer className="svc-wizard-footer">
              <div className="svc-wizard-footer__actions">
                {step > 1 && (
                  <button type="button" onClick={onPrev} className="btn-prev">
                    Anterior
                  </button>
                )}
                {step < 3 && (
                  <button
                    type="button"
                    onClick={onNext}
                    className="btn-next"
                    disabled={!isStepValid}
                  >
                    Siguiente
                  </button>
                )}
                {step === 3 && (
                  <button
                    type="button"
                    className="btn-save"
                    onClick={handleSave}
                    disabled={savingLocked}
                  >
                    Guardar
                  </button>
                )}
                <ButtonCancelForm
                  callbackCancel={onCancel}
                  locked={savingLocked}
                  confirmCancel={false}
                />
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceCreationWizard;
